import {useCallback, useMemo} from "react";
import {
  containerAlertTypes,
  DatabaseCode,
  siteAlertTypes,
  transportCategories,
  valveTypes
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {BaseMultiSelect, BaseMultiSelectProps} from "../base-multi-select";

export type BaseDbCodeMultiSelectProps<D extends DatabaseCode> =
  Omit<BaseMultiSelectProps, "defaultValue" | "options" | "data">
  & {
  defaultValue?: D[];
  options: D[];
  onDbCodeChange?: (dbCode: D[]) => void;
  sort?: boolean
};

export function BaseDbCodeMultiSelect<D extends DatabaseCode>(
  props: BaseDbCodeMultiSelectProps<D>
) {
  const {
    defaultValue,
    options,
    onChange,
    onDbCodeChange,
    sort = true,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  const _onChange = useCallback(
    (value: string[]) => {
      if (onDbCodeChange) {
        let items: D[] = [];

        if (value) {
          items = options.filter((d) => value.find((v) => v === d.code) !== undefined);
        }

        onDbCodeChange(items);
      }

      if (onChange) {
        onChange(value);
      }
    },
    [options, onChange, onDbCodeChange]
  );

  const data = useMemo(() => {
    const data = options.map((type) => {
      return {
        value: type.code,
        label: t<string>(type.displayValue.id, type.displayValue.defaultMessage)
      };
    })

    if (!sort) {
      return data
    }

    return data.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
  }, [options, t, sort]);

  return (
    <BaseMultiSelect
      data={data}
      onChange={_onChange}
      value={defaultValue?.map(value => value.code)}
      {...rest} />
  );
}

export type DbCodeMultiSelectProps<T extends DatabaseCode> = Omit<BaseDbCodeMultiSelectProps<T>, 'options'> & {
  options?: T[];
};


type _DbCodeMultiSelectProps = Omit<DbCodeMultiSelectProps<DatabaseCode>, 'options'> & {
  codeType: string
};

export const DbCodeMultiSelect = (props: _DbCodeMultiSelectProps) => {
  const {codeType, ...rest} = props;

  const options: DatabaseCode[] = useMemo(() => {
    if (codeType === 'TRANSPORT_CATEGORY') {
      return transportCategories
    } else if (codeType === 'VALVE_TYPE') {
      return valveTypes
    } else if (codeType === 'FIELD_ALERT_DETECTION') {
      return [
        {code: 'MANUAL', displayValue: {id: 'common.entity.fieldalert.detection.manual', defaultMessage: 'Manual'}},
        {code: 'AUTO', displayValue: {id: 'common.entity.fieldalert.detection.auto', defaultMessage: 'Auto'}}
      ]
    } else if (codeType === "FIELD_ALERT_TYPE") {
      return [
        ...Object.values(containerAlertTypes),
        ...Object.values(siteAlertTypes)
      ]
    }

    return []
  }, [codeType])

  return (
    <BaseDbCodeMultiSelect
      {...rest}
      options={options}/>
  )
};
